import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, errorMessage } from "../../api";
import {
  BINDING_MODES,
  type BindingMode,
  type Connection,
  type JsonSchemaObject,
  type Manifest,
  type RemoteCollection,
} from "../../api/types";
import { ConnectorBadge, EmptyState, ErrorBox, Modal, Spinner } from "../../components/bits";
import { cleanValues, SchemaFields, schemaDefaults, type FormValues } from "../../components/SchemaForm";
import { connectorLabel } from "../../lib/format";
import { isAdmin, loadCollections, toast, toastError } from "../../store/app";
import { useApp } from "../../store/hooks";

const DECK = "nextcloud-deck";
const MSTODO = "msgraph-todo";

/**
 * ASSUMPTION: used only if the server manifest for nextcloud-deck has no schemas.
 * Field names: config.base_url, credentials.username + credentials.password (matches BasicCredentials).
 */
const DECK_FALLBACK: { config: JsonSchemaObject; credentials: JsonSchemaObject } = {
  config: {
    type: "object",
    properties: { base_url: { type: "string", title: "Nextcloud URL", format: "uri" } },
    required: ["base_url"],
  },
  credentials: {
    type: "object",
    properties: {
      username: { type: "string", title: "Username" },
      password: {
        type: "string",
        title: "App password",
        format: "password",
        description: "Create one in Nextcloud → Settings → Security → Devices & sessions.",
      },
    },
    required: ["username", "password"],
  },
};

function manifestName(m: Manifest | undefined, type: string): string {
  return (m?.display_name as string | undefined) ?? (m?.name as string | undefined) ?? connectorLabel(type);
}

function isOAuth(m: Manifest): boolean {
  const auth = [m.auth, m.auth_kinds, (m as { auth_kind?: unknown }).auth_kind].flat().filter(Boolean).map(String);
  return auth.some((a) => /oauth/i.test(a)) || m.connector_type === MSTODO;
}

function AddConnectionDialog({
  connectorType,
  manifest,
  onClose,
  onCreated,
}: {
  connectorType: string;
  manifest: Manifest | undefined;
  onClose: () => void;
  onCreated: (c: Connection) => void;
}) {
  const orgId = useApp((s) => s.orgId);
  const admin = isAdmin();
  const fallback = connectorType === DECK ? DECK_FALLBACK : { config: null, credentials: null };
  const configSchema = manifest?.config_schema ?? fallback.config;
  const credSchema = manifest?.credential_schema ?? fallback.credentials;
  const label = manifestName(manifest, connectorType);
  const [name, setName] = useState(label);
  const [scope, setScope] = useState<"org" | "user">(admin ? "org" : "user");
  const [config, setConfig] = useState<FormValues>(() => schemaDefaults(configSchema));
  const [creds, setCreds] = useState<FormValues>(() => schemaDefaults(credSchema));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <Modal title={`Add ${label}`} onClose={onClose}>
      <form
        className="form"
        onSubmit={async (e) => {
          e.preventDefault();
          if (!orgId) return;
          setBusy(true);
          setError(null);
          try {
            const c = await api.createConnection(orgId, {
              connector_type: connectorType,
              scope,
              name: name.trim() || label,
              config: cleanValues(config),
              credentials: cleanValues(creds),
            });
            onCreated(c);
          } catch (err) {
            setError(errorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      >
        <label className="field">
          <span>Name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} required />
        </label>
        <fieldset className="field radio-row">
          <legend>Scope</legend>
          <label>
            <input type="radio" name="scope" checked={scope === "org"} disabled={!admin} onChange={() => setScope("org")} />
            Organization (shared)
          </label>
          <label>
            <input type="radio" name="scope" checked={scope === "user"} onChange={() => setScope("user")} />
            Only me
          </label>
        </fieldset>
        <SchemaFields schema={configSchema} values={config} onChange={setConfig} idPrefix="cfg" />
        <SchemaFields schema={credSchema} values={creds} onChange={setCreds} idPrefix="cred" />
        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        <div className="form-actions">
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn primary" disabled={busy}>
            {busy ? "Connecting…" : "Connect"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function ImportDialog({ connection, onClose }: { connection: Connection; onClose: () => void }) {
  const spaces = useApp((s) => s.spaces) ?? [];
  const currentSpaceId = useApp((s) => s.currentSpaceId);
  const navigate = useNavigate();
  const [remote, setRemote] = useState<RemoteCollection[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [mode, setMode] = useState<BindingMode>("mirror");
  const [spaceId, setSpaceId] = useState(currentSpaceId ?? spaces[0]?.id ?? "");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .remoteCollections(connection.id)
      .then((r) => setRemote(r.collections))
      .catch((e) => setError(errorMessage(e)));
  }, [connection.id]);

  const toggle = (id: string) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  return (
    <Modal title={`Import from ${connection.name}`} onClose={onClose} wide>
      {error && <ErrorBox error={error} />}
      {!remote && !error && <Spinner label="Loading remote collections…" />}
      {remote && (
        <form
          className="form"
          onSubmit={async (e) => {
            e.preventDefault();
            if (!selected.size || !spaceId) return;
            setBusy(true);
            try {
              const res = await api.importCollections(connection.id, {
                space_id: spaceId,
                remote_collection_ids: [...selected],
                mode,
              });
              await loadCollections(spaceId);
              toast(`Imported ${res.collections.length} collection(s)`, "success");
              onClose();
              const first = res.collections[0];
              if (first) navigate(`/c/${first.collection.id}`);
            } catch (err) {
              toastError(err, "Import failed");
            } finally {
              setBusy(false);
            }
          }}
        >
          {remote.length === 0 ? (
            <p className="muted">No remote collections found.</p>
          ) : (
            <fieldset className="remote-list">
              <legend>Remote collections</legend>
              {remote.map((r) => (
                <label key={r.remote_id} className={`remote-row${r.bound_collection_id ? " bound" : ""}`}>
                  <input
                    type="checkbox"
                    checked={selected.has(r.remote_id)}
                    disabled={!!r.bound_collection_id}
                    onChange={() => toggle(r.remote_id)}
                  />
                  <span className="remote-name">{r.name}</span>
                  <span className="muted small">{r.kind}</span>
                  {r.bound_collection_id && <span className="badge tiny">already imported</span>}
                  {r.url && (
                    <a href={r.url} target="_blank" rel="noreferrer noopener" className="small">
                      open
                    </a>
                  )}
                </label>
              ))}
            </fieldset>
          )}
          <div className="field-row">
            <label className="field">
              <span>Sync mode</span>
              <select value={mode} onChange={(e) => setMode(e.target.value as BindingMode)}>
                {BINDING_MODES.map((m) => (
                  <option key={m} value={m}>
                    {m.replace(/_/g, " ")}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Target space</span>
              <select value={spaceId} onChange={(e) => setSpaceId(e.target.value)} required>
                {spaces.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="form-actions">
            <button type="button" className="btn" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn primary" disabled={busy || selected.size === 0}>
              {busy ? "Importing…" : `Import ${selected.size || ""}`}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}

const HEALTH_TEXT: Record<Connection["health"], string> = {
  ok: "Healthy",
  error: "Error",
  needs_auth: "Needs sign-in",
  unknown: "Unknown",
};

export function ConnectionsPage() {
  const orgId = useApp((s) => s.orgId);
  const [params, setParams] = useSearchParams();
  const [connections, setConnections] = useState<Connection[] | null>(null);
  const [manifests, setManifests] = useState<Manifest[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState<string | null>(null);
  const [importing, setImporting] = useState<Connection | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = () => {
    if (!orgId) return;
    setError(null);
    api
      .connections(orgId)
      .then((r) => setConnections(r.connections))
      .catch((e) => setError(errorMessage(e)));
  };

  useEffect(() => {
    load();
    api
      .connectors()
      .then((r) => setManifests(r.connectors))
      .catch(() => setManifests([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  // OAuth callback redirect lands here with ?connected= or ?error=
  useEffect(() => {
    const connected = params.get("connected");
    const err = params.get("error");
    if (connected) toast("Connection established", "success");
    if (err) toast(`Connection failed: ${err}`, "error", 10000);
    if (connected || err) {
      const next = new URLSearchParams(params);
      next.delete("connected");
      next.delete("error");
      setParams(next, { replace: true });
    }
  }, [params, setParams]);

  const manifestByType = useMemo(() => new Map(manifests.map((m) => [m.connector_type, m])), [manifests]);
  const formConnectors = useMemo(() => {
    const types = new Set<string>([DECK]);
    for (const m of manifests) if (!isOAuth(m)) types.add(m.connector_type);
    return [...types];
  }, [manifests]);
  const oauthConnectors = useMemo(() => {
    const types = new Set<string>([MSTODO]);
    for (const m of manifests) if (isOAuth(m)) types.add(m.connector_type);
    return [...types];
  }, [manifests]);

  const startOAuth = (type: string) => {
    if (!orgId) return;
    const name = type === MSTODO ? "Microsoft To Do" : manifestName(manifestByType.get(type), type);
    window.location.href = api.oauthStartUrl(orgId, type, "user", name);
  };

  const act = async (c: Connection, fn: () => Promise<void>) => {
    setBusyId(c.id);
    try {
      await fn();
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section aria-labelledby="conn-title">
      <h2 id="conn-title" className="sr-only">
        Connections
      </h2>
      <div className="add-row">
        {formConnectors.map((t) => (
          <button key={t} type="button" className="btn" onClick={() => setAdding(t)}>
            Add {manifestName(manifestByType.get(t), t)}
          </button>
        ))}
        {oauthConnectors.map((t) => (
          <button key={t} type="button" className="btn primary" onClick={() => startOAuth(t)}>
            Connect {t === MSTODO ? "Microsoft To Do" : manifestName(manifestByType.get(t), t)}
          </button>
        ))}
      </div>

      {error && <ErrorBox error={error} onRetry={load} />}
      {connections === null && !error ? (
        <Spinner />
      ) : connections && connections.length === 0 ? (
        <EmptyState title="No connections yet">
          <p className="muted">Connect Nextcloud Deck or Microsoft To Do to pull in your existing boards and lists.</p>
        </EmptyState>
      ) : (
        <ul className="conn-list">
          {connections?.map((c) => (
            <li key={c.id} className="conn-card">
              <div className="conn-main">
                <div className="conn-title">
                  <ConnectorBadge type={c.connector_type} />
                  <strong>{c.name}</strong>
                  <span className={`health ${c.health}`}>
                    <span className="dot" aria-hidden="true" />
                    {HEALTH_TEXT[c.health]}
                  </span>
                </div>
                <div className="muted small">
                  {c.scope === "org" ? "Shared with organization" : "Personal"}
                  {c.remote_tenant && ` · ${c.remote_tenant}`}
                  {typeof c.config.base_url === "string" && ` · ${c.config.base_url}`}
                </div>
                {c.last_error && <div className="form-error small">{c.last_error}</div>}
              </div>
              <div className="conn-actions">
                {c.health === "needs_auth" && oauthConnectors.includes(c.connector_type) && (
                  <button type="button" className="btn small primary" onClick={() => startOAuth(c.connector_type)}>
                    Reconnect
                  </button>
                )}
                <button
                  type="button"
                  className="btn small"
                  disabled={busyId === c.id}
                  onClick={() =>
                    act(c, async () => {
                      try {
                        const updated = await api.testConnection(c.id);
                        setConnections((cs) => cs?.map((x) => (x.id === c.id ? updated : x)) ?? null);
                        toast(
                          updated.health === "ok" ? "Connection OK" : `Connection: ${HEALTH_TEXT[updated.health]}`,
                          updated.health === "ok" ? "success" : "error",
                        );
                      } catch (e) {
                        toastError(e, "Test failed");
                      }
                    })
                  }
                >
                  Test
                </button>
                <button type="button" className="btn small" onClick={() => setImporting(c)}>
                  Import…
                </button>
                <button
                  type="button"
                  className="btn small"
                  disabled={busyId === c.id}
                  onClick={() =>
                    act(c, async () => {
                      try {
                        const r = await api.importUsers(c.id);
                        toast(`Imported ${r.identities.length} user identities`, "success");
                      } catch (e) {
                        toastError(e, "Import users failed");
                      }
                    })
                  }
                >
                  Import users
                </button>
                <button
                  type="button"
                  className="btn small danger"
                  disabled={busyId === c.id}
                  onClick={() =>
                    act(c, async () => {
                      if (!confirm(`Delete connection "${c.name}"? Its bindings are removed; local items stay.`)) return;
                      try {
                        await api.deleteConnection(c.id);
                        setConnections((cs) => cs?.filter((x) => x.id !== c.id) ?? null);
                      } catch (e) {
                        toastError(e, "Delete failed");
                      }
                    })
                  }
                >
                  Delete
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {adding && (
        <AddConnectionDialog
          connectorType={adding}
          manifest={manifestByType.get(adding)}
          onClose={() => setAdding(null)}
          onCreated={(c) => {
            setAdding(null);
            setConnections((cs) => [...(cs ?? []), c]);
            toast(
              c.health === "ok" ? `Connected ${c.name}` : `${c.name} added (${HEALTH_TEXT[c.health]})`,
              c.health === "ok" ? "success" : "error",
            );
          }}
        />
      )}
      {importing && <ImportDialog connection={importing} onClose={() => setImporting(null)} />}
    </section>
  );
}
